import { c as _c } from "react/compiler-runtime";
import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Type } from '@repo/design-system/components/ui/type';
import { css } from '@repo/panda/css';
import type { SystemStyleObject } from '@repo/panda/types';
import type React from 'react';
import { Fragment, type ReactNode } from 'react';
interface MapTextSuggestionPanelProps {
  label: string;
  phase: 'idle' | 'debouncing' | 'streaming' | 'ready' | 'error';
  suggestion: string;
  currentValue?: string;
  error: string | null;
  className?: SystemStyleObject;
  onAccept: () => void;
  onDismiss: () => void;
  onRetry: () => void;
}
const panelStyles: SystemStyleObject = {
  gap: '12',
  marginTop: '8',
  padding: '12',
  width: 'full',
  minWidth: '0',
  borderRadius: '12',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'whiteAlpha.10',
  backgroundColor: 'blackAlpha.40',
  boxShadow: 'md'
};
const suggestionBodyStyles = css({
  width: 'full',
  minWidth: '0',
  whiteSpace: 'pre-wrap',
  color: 'whiteAlpha.80',
  fontSize: 'sm',
  lineHeight: '[1.6]',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  paddingY: '4'
});
const changedTokenStyles = css({
  backgroundColor: 'success.300/20',
  color: 'white',
  borderRadius: '4',
  paddingX: '2',
  boxShadow: '[inset 0 -1px 0 token(colors.success.300)]'
});
const newlineMarkerStyles = css({
  color: 'success.300',
  fontSize: 'xs',
  marginX: '2',
  opacity: 0.75
});
const statusRowStyles: SystemStyleObject = {
  paddingY: '4'
};
const successIconStyles = css({
  color: 'success'
});
const neutralIconStyles = css({
  color: 'whiteAlpha.70'
});
const generatingIconStyles = css({
  color: 'whiteAlpha.80',
  animation: '[subtle-pulse 1.6s ease-in-out infinite]'
});
const generatingTextStyles = css({
  animation: '[subtle-pulse 1.6s ease-in-out infinite]'
});
const headerRowStyles: SystemStyleObject = {
  paddingBottom: '8'
};
const actionsRowStyles: SystemStyleObject = {
  marginTop: '4'
};
const disclaimerStyles = css({
  color: 'whiteAlpha.50',
  fontSize: 'xs',
  lineHeight: '[1.4]',
  marginTop: '12',
  textAlign: 'center'
});
interface DiffToken {
  text: string;
  changed: boolean;
}
function tokenize(value: string): string[] {
  return value.split(/(\s+)/).filter(part => part.length > 0);
}
function diffAgainstCurrent(before: string, after: string): DiffToken[] {
  const beforeTokens = tokenize(before);
  const afterTokens = tokenize(after);
  if (beforeTokens.length === 0) {
    return afterTokens.map(text => ({
      text,
      changed: true
    }));
  }
  const m = beforeTokens.length;
  const n = afterTokens.length;
  const stride = n + 1;
  const lcs = new Int32Array((m + 1) * stride);
  const read = (row: number, col: number): number => lcs[row * stride + col] ?? 0;
  for (let i = 1; i <= m; i++) {
    const beforeToken = beforeTokens[i - 1];
    for (let j = 1; j <= n; j++) {
      lcs[i * stride + j] = beforeToken === afterTokens[j - 1] ? read(i - 1, j - 1) + 1 : Math.max(read(i - 1, j), read(i, j - 1));
    }
  }
  const isCommon = new Array<boolean>(n).fill(false);
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (beforeTokens[i - 1] === afterTokens[j - 1]) {
      isCommon[j - 1] = true;
      i--;
      j--;
    } else if (read(i - 1, j) >= read(i, j - 1)) {
      i--;
    } else {
      j--;
    }
  }
  return afterTokens.map((text, index) => ({
    text,
    changed: !isCommon[index]
  }));
}
function renderChangedWhitespace(text: string, keyPrefix: string): ReactNode {
  const segments: ReactNode[] = [];
  let bufferStart = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\n') {
      if (i > bufferStart) {
        segments.push(<span key={`${keyPrefix}-s${i}`} className={changedTokenStyles}>
                        {text.slice(bufferStart, i)}
                    </span>);
      }
      segments.push(<span key={`${keyPrefix}-n${i}`} className={newlineMarkerStyles} aria-hidden="true">
                    ↵
                </span>);
      segments.push('\n');
      bufferStart = i + 1;
    }
  }
  if (bufferStart < text.length) {
    segments.push(<span key={`${keyPrefix}-s${bufferStart}`} className={changedTokenStyles}>
                {text.slice(bufferStart)}
            </span>);
  }
  return <Fragment key={keyPrefix}>{segments}</Fragment>;
}
function renderDiffToken(token: DiffToken, index: number): ReactNode {
  if (!token.changed) {
    return <Fragment key={index}>{token.text}</Fragment>;
  }
  if (token.text.includes('\n')) {
    return renderChangedWhitespace(token.text, `t${index}`);
  }
  return <span key={index} className={changedTokenStyles}>
            {token.text}
        </span>;
}
export const MapTextSuggestionPanel: React.FC<MapTextSuggestionPanelProps> = t0 => {
  const $ = _c(32);
  const {
    label,
    phase,
    suggestion,
    currentValue,
    error,
    className,
    onAccept,
    onDismiss,
    onRetry
  } = t0;
  let T0;
  let showNoChangesState;
  let t1;
  let t2;
  let t3;
  if ($[0] !== className || $[1] !== currentValue || $[2] !== error || $[3] !== label || $[4] !== phase || $[5] !== suggestion) {
    const diffedSuggestion = suggestion && currentValue !== undefined ? diffAgainstCurrent(currentValue, suggestion) : null;
    const hasChanges = diffedSuggestion ? diffedSuggestion.some(_temp) : Boolean(suggestion);
    showNoChangesState = phase === "ready" && diffedSuggestion !== null && !hasChanges;
    const showStreamingContent = Boolean(suggestion) && !showNoChangesState && phase !== "error";
    let statusIcon = null;
    let statusText = null;
    let statusIconClass;
    if (phase === "error") {
      statusIcon = "error";
      statusText = error ?? "Suggestion failed";
    } else {
      if (showNoChangesState) {
        statusIcon = "check_circle";
        statusText = "No changes suggested";
        statusIconClass = successIconStyles;
      } else {
        if (!suggestion) {
          if (phase === "debouncing") {
            statusIcon = "hourglass_empty";
            statusText = "Waiting for pause in typing...";
            statusIconClass = neutralIconStyles;
          } else {
            if (phase === "streaming") {
              statusIcon = "auto_awesome";
              statusText = "Generating suggestion...";
              statusIconClass = generatingIconStyles;
            }
          }
        }
      }
    }
    T0 = FlexColumn;
    if ($[11] !== className) {
      t1 = css.raw({
        ...panelStyles,
        ...className
      });
      $[11] = className;
      $[12] = t1;
    } else {
      t1 = $[12];
    }
    let t4;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = css.raw(headerRowStyles);
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t5;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Icon icon="prompt_suggestion" size="sm" decorative={true} className={css({
        color: "white"
      })} />;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    if ($[15] !== label) {
      t2 = <FlexRow align="center" gap="8" className={t4}>{t5}<Type variant="caption" color="whiteAlpha.80">{label}</Type></FlexRow>;
      $[15] = label;
      $[16] = t2;
    } else {
      t2 = $[16];
    }
    t3 = showStreamingContent ? <div className={suggestionBodyStyles}>{diffedSuggestion ? diffedSuggestion.map(_temp2) : suggestion}</div> : statusIcon && statusText ? <FlexRow align="center" gap="8" className={css.raw(statusRowStyles)}><Icon icon={statusIcon} size="sm" className={statusIconClass} decorative={true} /><Type variant="body-s" color={phase === "error" ? "error" : "whiteAlpha.80"} className={phase === "streaming" && !suggestion ? generatingTextStyles : undefined}>{statusText}</Type></FlexRow> : null;
    $[0] = className;
    $[1] = currentValue;
    $[2] = error;
    $[3] = label;
    $[4] = phase;
    $[5] = suggestion;
    $[6] = T0;
    $[7] = showNoChangesState;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    T0 = $[6];
    showNoChangesState = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
  }
  let t4;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = css.raw(actionsRowStyles);
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== onAccept || $[19] !== onDismiss || $[20] !== onRetry || $[21] !== phase || $[22] !== showNoChangesState || $[23] !== suggestion) {
    t5 = <FlexRow gap="8" justify="end" className={t4}>{phase === "error" ? <><Button onClick={onDismiss} color="secondary" size="sm">Dismiss</Button><Button onClick={onRetry} color="white" size="sm">Retry</Button></> : <><Button onClick={onDismiss} color="secondary" size="sm">Dismiss</Button><Button onClick={onRetry} color="secondary" size="sm">Regenerate</Button>{phase === "ready" && !showNoChangesState && <Button onClick={onAccept} color="white" size="sm" disabled={!suggestion}>Apply</Button>}</>}</FlexRow>;
    $[18] = onAccept;
    $[19] = onDismiss;
    $[20] = onRetry;
    $[21] = phase;
    $[22] = showNoChangesState;
    $[23] = suggestion;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className={disclaimerStyles}>Review before applying — AI can miss context.</div>;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== T0 || $[27] !== t1 || $[28] !== t2 || $[29] !== t3 || $[30] !== t5) {
    t7 = <T0 className={t1}>{t2}{t3}{t5}{t6}</T0>;
    $[26] = T0;
    $[27] = t1;
    $[28] = t2;
    $[29] = t3;
    $[30] = t5;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  return t7;
};
function _temp(token) {
  return token.changed;
}
function _temp2(token_0, index) {
  return renderDiffToken(token_0, index);
}